import React from 'react';

import { Box, TextField } from '@mui/material';

export const TACTICAL_WINDOWS = [
  { id: 'tac_campaign_start', label: 'Campaign start date', from: '2024-02-09', to: '2099-12-31' },
  { id: 'tac_campaign_end', label: 'Campaign end date', from: '2024-01-31', to: '2031-12-31' },
  { id: 'tac_prior_start', label: 'Prior start date', from: '2024-01-07', to: '2099-12-31' },
  { id: 'tac_prior_end', label: 'Prior end date', from: '2024-02-08', to: '2099-12-31' },
] as const;

export function tacticalWindows(params: URLSearchParams) {
  return Object.fromEntries(
    TACTICAL_WINDOWS.map(({ id, from, to }) => [
      id,
      {
        from: params.get(`${id}_from`) ?? from,
        to: params.get(`${id}_to`) ?? to,
      },
    ]),
  );
}

export function tacticalWindowProblem(params: URLSearchParams): string | null {
  const ranges = tacticalWindows(params);
  for (const { id, label } of TACTICAL_WINDOWS) {
    const range = ranges[id];
    if (
      ![range.from, range.to].every(
        (date) =>
          /^\d{4}-\d{2}-\d{2}$/.test(date) &&
          !Number.isNaN(Date.parse(date)) &&
          new Date(date).toISOString().slice(0, 10) === date,
      )
    )
      return `Enter valid dates for ${label.toLowerCase()}.`;
    if (range.from > range.to) return 'Each window must start on or before its end date.';
  }
  return null;
}

export function clearTacticalWindows(params: URLSearchParams) {
  const next = new URLSearchParams(params);
  for (const { id } of TACTICAL_WINDOWS) {
    next.delete(`${id}_from`);
    next.delete(`${id}_to`);
  }
  return next;
}

export function TacticalWindows({
  params,
  onChange,
}: {
  params: URLSearchParams;
  onChange: (params: URLSearchParams) => void;
}) {
  const ranges = tacticalWindows(params);
  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: 'pointer', py: 1 }}>
        Campaign and prior date windows
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, pt: 1 }}>
        {TACTICAL_WINDOWS.map(({ id, label }) => (
          <Box key={id} sx={{ display: 'flex', gap: 1 }}>
            {(['from', 'to'] as const).map((bound) => (
              <TextField
                key={bound}
                size="small"
                type="date"
                label={`${label} ${bound}`}
                value={ranges[id][bound]}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => {
                  const next = new URLSearchParams(params);
                  next.set(`${id}_${bound}`, event.target.value);
                  onChange(next);
                }}
                sx={{ flex: 1, minWidth: 0 }}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
